import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Stethoscope,
  Send,
  Loader2,
  AlertTriangle,
  Heart,
  Brain,
  Activity,
  Thermometer,
  User,
  FileText,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

import { API_URL } from '../config';

export default function DiagnosticPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  
  // Form state
  const [symptoms, setSymptoms] = useState('');
  const [age, setAge] = useState(isAdult ? '45' : '5');
  const [sex, setSex] = useState('Homme');
  const [medicalHistory, setMedicalHistory] = useState('');
  const [vitalSigns, setVitalSigns] = useState('');
  
  // Results state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeSymptoms = async () => {
    if (!symptoms.trim()) {
      toast.error('Veuillez décrire les symptômes');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/diagnostic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          symptoms: symptoms.trim(),
          age: parseInt(age) || (isAdult ? 45 : 5),
          sex,
          medical_history: medicalHistory.trim(),
          vital_signs: vitalSigns.trim(),
          audience
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('Error analyzing symptoms:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSymptoms('');
    setAge(isAdult ? '45' : '5');
    setSex('Homme');
    setMedicalHistory('');
    setVitalSigns('');
    setResult(null);
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30">
            <Stethoscope className="w-8 h-8 text-teal-400" />
          </div>
          Aide au Diagnostic
        </h1>
        <p className="text-[hsl(var(--text-dim))]">
          Analyse des symptômes et orientation diagnostique
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {/* Left Column - Patient Data Form */}
        <div className="glass-card rounded-2xl p-6 border border-[hsl(var(--border))]">
          <div className="flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold">Données patient</h2>
          </div>
          <p className="text-sm text-[hsl(var(--text-dim))] mb-6">
            Renseignez les informations cliniques
          </p>

          <div className="space-y-5">
            {/* Symptoms */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Symptômes <span className="text-red-400">*</span>
              </label>
              <Textarea
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                placeholder="Ex: Douleur thoracique intense, dyspnée..."
                className="bg-white/5 border-[hsl(var(--border))] min-h-[100px] resize-none"
                data-testid="diagnostic-symptoms-input"
              />
            </div>

            {/* Age and Sex Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Âge <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="45"
                  className="bg-white/5 border-[hsl(var(--border))]"
                  data-testid="diagnostic-age-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sexe</label>
                <Select value={sex} onValueChange={setSex}>
                  <SelectTrigger className="bg-white/5 border-[hsl(var(--border))]" data-testid="diagnostic-sex-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Homme">Homme</SelectItem>
                    <SelectItem value="Femme">Femme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Medical History */}
            <div>
              <label className="block text-sm font-medium mb-2">Antécédents médicaux</label>
              <Input
                value={medicalHistory}
                onChange={(e) => setMedicalHistory(e.target.value)}
                placeholder="Diabète, HTA, allergies..."
                className="bg-white/5 border-[hsl(var(--border))]"
                data-testid="diagnostic-history-input"
              />
            </div>

            {/* Vital Signs */}
            <div>
              <label className="block text-sm font-medium mb-2">Signes vitaux</label>
              <Input
                value={vitalSigns}
                onChange={(e) => setVitalSigns(e.target.value)}
                placeholder="FC: 120, TA: 90/60, FR: 24, SpO2: 95%"
                className="bg-white/5 border-[hsl(var(--border))]"
                data-testid="diagnostic-vitals-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={analyzeSymptoms}
              disabled={loading || !symptoms.trim()}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium hover:opacity-90 h-12"
              data-testid="diagnostic-analyze-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Analyser
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="glass-card rounded-2xl p-6 border border-[hsl(var(--border))]">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold">Résultats de l'analyse</h2>
          </div>
          <p className="text-sm text-[hsl(var(--text-dim))] mb-6">
            Diagnostic différentiel et conduite à tenir
          </p>

          {result ? (
            <div className="space-y-4 animate-scale-in" data-testid="diagnostic-results">
              {/* AI Response */}
              {result.analysis && (
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-xl p-4 border border-teal-500/20">
                    <div className="flex items-center gap-2 mb-3 text-teal-400">
                      <Brain className="w-5 h-5" />
                      <span className="font-medium">Analyse IA</span>
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-[hsl(var(--text))] font-sans leading-relaxed">
                      {result.analysis}
                    </pre>
                  </div>
                </div>
              )}

              {/* Differential Diagnoses */}
              {result.differential_diagnoses && result.differential_diagnoses.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-cyan-300 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Diagnostics différentiels
                  </h4>
                  <div className="space-y-2">
                    {result.differential_diagnoses.map((diag, i) => (
                      <div 
                        key={i} 
                        className={`p-3 rounded-lg border ${
                          i === 0 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : i === 1 
                              ? 'bg-amber-500/10 border-amber-500/30'
                              : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {i === 0 ? (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          ) : i === 1 ? (
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                          ) : (
                            <Info className="w-4 h-4 text-cyan-400" />
                          )}
                          <span className="font-medium">{diag}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {result.recommendations && result.recommendations.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Conduite à tenir
                  </h4>
                  <ul className="space-y-1">
                    {result.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[hsl(var(--text-dim))]">
                        <span className="text-green-400 mt-1">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Reset Button */}
              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full mt-4"
              >
                Nouvelle analyse
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--bg-elev))] flex items-center justify-center mb-4">
                <Stethoscope className="w-8 h-8 text-[hsl(var(--muted))]" />
              </div>
              <p className="text-[hsl(var(--text-dim))]">
                Les résultats apparaîtront ici
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-6xl mx-auto">
        <div className="glass-card rounded-lg p-4 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-400">Avertissement</p>
              <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
                Cette aide au diagnostic est un outil d'orientation et ne remplace en aucun cas l'examen clinique 
                et le jugement médical. Les suggestions générées par l'IA doivent être validées par un professionnel de santé.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
